import { useContext, lazy, Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/Login';
import LandingPage from './pages/LandingPage';
import Fleets from './pages/Fleets';
import PrivateRoute from './components/PrivateRoute';
import ForgotPassword from './pages/ForgotPassword';
import Units from './pages/Units';
import Layout from './components/Layout/Layout';
import ManageUnits from './pages/ManageUnits';
import { useLocation } from 'react-router-dom';
import ConfigureUnits from './components/ConfigureUnits';
import Dashboard from './pages/DashBoard';
import { EditUnit } from './pages/EditUnit';
import Alerts from './pages/Alerts';
import Energy from './pages/Energy';
import Control from './pages/Control';
import UnitDetails from './pages/UnitDetails';
import ManageProperties from './pages/ManageProperties';
import MyAccount from './pages/MyAccount';
import Billing from './pages/Billing';
import UserManagement  from './pages/UserManagement';
import PopupConfiguration from './pages/PopupConfiguration';
import CustomerManagement from './pages/CustomerManagement';
import LoadingSpinner from './components/Common/LoadingSpinner';
import CurrentUserContext from './contexts/CurrentUserContext';
import AccountPaymentRequired from './pages/AccountPaymentRequired';
import AccountInactive from './pages/AccountInactive';
import LicenseAgreement from './pages/LicenseAgreement';
import { trackPageView } from './lib/analytics';

const HomeRedirect = () => {
  const { currentUser } = useContext(CurrentUserContext);

  return (
    <Navigate
      to={currentUser?.userType === 'GradientAdmin' ? '/customer-management' : '/dashboard'}
      replace
    />
  );
};

// Loaded on demand: swagger-ui-react and its dependencies are several megabytes, and a
// static import puts all of it in the main bundle for every visitor of every page.
const ApiDocumentation = lazy(() => import('./pages/ApiDocumentation'));

function App() {
  const location = useLocation();
  const previousPageUrlRef = useRef(null);

  useEffect(() => {
    trackPageView(previousPageUrlRef.current);
    previousPageUrlRef.current = `${window.location.origin}${location.pathname}`;
  }, [location.pathname]);

  return (
    <Layout>
      <Routes location={location} key={location.pathname}>
        {/* <Route path="/fleets" element={<Fleets />} /> */}
        <Route path="/login" 
        element={
        <PrivateRoute key={location.pathname} isPublic>
          <LoginPage />
          </PrivateRoute>} 
          />
        <Route
          path="/landingpage"
          element={
            <PrivateRoute key={location.pathname} requiredUserType="Customer">
              <LandingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/"
          element={
            <PrivateRoute key={location.pathname}>
              <HomeRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/units"
          element={
            <PrivateRoute key={location.pathname} requiredUserType="Customer">
              <Units />
            </PrivateRoute>
          }
        />
        <Route
          path="/managepod"
          element={
            <PrivateRoute key={location.pathname} requiredUserType="Customer">
              <Fleets />
            </PrivateRoute>
          }
        />
        <Route
          path="/units/:serialNumber"
          element={
            <PrivateRoute key={location.pathname} requiredUserType="Customer">
              <UnitDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/manageunits"
          element={
            <PrivateRoute key={location.pathname} requiredUserType="Customer">
              <ManageUnits />
            </PrivateRoute>
          }
        />
        <Route
          path="/manageproperties"
          element={
            <PrivateRoute key={location.pathname} requiredUserType="Customer">
              <ManageProperties />
            </PrivateRoute>
          }
        />
        <Route
          path="/EditUnit"
          element={
            <PrivateRoute key={location.pathname} requiredUserType="Customer">
              <EditUnit />
            </PrivateRoute>
          }
        />
        <Route
          path="/configureUnits"
          element={
            <PrivateRoute key={location.pathname} requiredUserType="Customer">
              <ConfigureUnits />
            </PrivateRoute>
          }
        />
        <Route path="/dashboard" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer">
            <Dashboard />
          </PrivateRoute>
        } />
        <Route path="/popup-configuration" element={
          <PrivateRoute key={location.pathname} requiredUserType="GradientAdmin">
            <PopupConfiguration />
          </PrivateRoute>
        } />
        <Route path="/popup-configuration/:popupId" element={
          <PrivateRoute key={location.pathname} requiredUserType="GradientAdmin">
            <PopupConfiguration />
          </PrivateRoute>
        } />
        <Route path="/customer-management" element={
          <PrivateRoute key={location.pathname} requiredUserType="GradientAdmin">
            <CustomerManagement />
          </PrivateRoute>
        } />
        <Route path="/customer-management/:customerId" element={
          <PrivateRoute key={location.pathname} requiredUserType="GradientAdmin">
            <CustomerManagement />
          </PrivateRoute>
        } />
        <Route path="/alerts" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer"><Alerts /></PrivateRoute>
        } />
        <Route path="/energy" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer"> <Energy /></PrivateRoute>
        } />
        <Route path="/control" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer">
            <Control />
          </PrivateRoute>} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route
          path="/api-docs"
          element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-64"><LoadingSpinner /></div>}>
              <ApiDocumentation />
            </Suspense>
          }
        />
        <Route path="/myaccount" element={
          <PrivateRoute key={location.pathname}>
            <MyAccount />
          </PrivateRoute>} />
        <Route path="/billing" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer" requiredAccessType="FULL_ACCESS">
            <Billing />
          </PrivateRoute>} />
        <Route path="/account-payment-required" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer">
            <AccountPaymentRequired />
          </PrivateRoute>} />
        <Route path="/account-inactive" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer">
            <AccountInactive />
          </PrivateRoute>} />
        <Route path="/license-agreement" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer">
            <LicenseAgreement />
          </PrivateRoute>} />
        <Route path="/usermanagement" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer" requiredAccessType="FULL_ACCESS">
            <UserManagement />
          </PrivateRoute>} />
        <Route path="*" element={
          <PrivateRoute key={location.pathname} requiredUserType="Customer">
            <Dashboard />
          </PrivateRoute>
        } />
      </Routes>
    </Layout>
  );
}

export default App;
